import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, Save, BookOpen, } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getUser, setUser, getEnrolledCourses } from '@/lib/data';

export default function Profile() {
  const currentUser = getUser() || { name: '', email: '', role: 'student' };
  const [profile, setProfile] = useState({
    name: currentUser.name || '',
    email: currentUser.email || '',
    phone: currentUser.phone || '',
    location: currentUser.location || '',
    bio: currentUser.bio || '',
  });
  const [saved, setSaved] = useState(false);

  const enrolled = getEnrolledCourses();

  const handleSave = () => {
    setUser({ ...currentUser, ...profile });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <h1 className="font-heading text-2xl font-bold">Profile</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="bg-card rounded-xl border p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <span className="font-heading text-2xl font-bold text-primary">{profile.name[0]?.toUpperCase() || 'U'}</span>
          </div>
          <h2 className="font-heading font-semibold">{profile.name || 'User'}</h2>
          <p className="text-sm text-muted-foreground capitalize">{currentUser.role}</p>

          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
            <div>
              <p className="font-heading text-xl font-bold">{enrolled.length}</p>
              <p className="text-xs text-muted-foreground">Courses</p>
            </div>
            <div>
              <p className="font-heading text-xl font-bold">{enrolled.filter(e => e.progress >= 100).length}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
            <div>
              <p className="font-heading text-xl font-bold">{enrolled.length * 12}h</p>
              <p className="text-xs text-muted-foreground">Learned</p>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="lg:col-span-2 bg-card rounded-xl border p-6 space-y-4">
          <h2 className="font-heading font-semibold">Edit Profile</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} className="pl-10" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} className="pl-10" />
              </div>
            </div>
            <div>
              <Label>Phone</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} className="pl-10" placeholder="+1 (555) 000-0000" />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={profile.location} onChange={e => setProfile({ ...profile, location: e.target.value })} className="pl-10" placeholder="City, Country" />
              </div>
            </div>
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea value={profile.bio} onChange={e => setProfile({ ...profile, bio: e.target.value })} placeholder="Tell us about yourself..." className="mt-1" />
          </div>
          <Button onClick={handleSave} className="gap-2">
            <Save className="w-4 h-4" /> {saved ? 'Saved ✓' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Learning History */}
      <div className="bg-card rounded-xl border p-6">
        <h2 className="font-heading font-semibold mb-4">Learning History</h2>
        {enrolled.length > 0 ? (
          <div className="space-y-3">
            {enrolled.map((e, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <BookOpen className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">Course ID: {e.courseId}</p>
                  <p className="text-xs text-muted-foreground">Enrolled {new Date(e.enrolledDate).toLocaleDateString()}</p>
                </div>
                <span className="text-sm font-medium">{e.progress}%</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No learning history yet.</p>
        )}
      </div>
    </motion.div>
  );
}